/**
 * SCROLL ANIMATIONS - Système complet
 * Gère toutes les animations au scroll
 */

class ScrollAnimations {
  constructor(options = {}) {
    this.options = {
      threshold: options.threshold || 0.15,
      rootMargin: options.rootMargin || '0px 0px -50px 0px',
      once: options.once || false,
      ...options
    };
    
    this.elements = document.querySelectorAll('[data-aos]');
    this.parallaxElements = document.querySelectorAll('[data-aos="parallax"]');
    
    this.init();
    this.initParallax();
  }

  init() {
    if (!this.elements.length) return;
    
    // Créer l'Intersection Observer
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Ajouter la classe d'animation
          entry.target.classList.add('aos-animate');
          
          // Si data-aos-once ou option globale once, arrêter d'observer
          const shouldObserveOnce = entry.target.dataset.aosOnce !== undefined || this.options.once;
          
          if (shouldObserveOnce) {
            this.observer.unobserve(entry.target);
          }
        } else {
          // Réinitialiser l'animation si pas "once"
          const shouldObserveOnce = entry.target.dataset.aosOnce !== undefined || this.options.once;
          
          if (!shouldObserveOnce) {
            entry.target.classList.remove('aos-animate');
          }
        }
      });
    }, this.options);

    // Observer tous les éléments
    this.elements.forEach(el => {
      // Vérifier si on doit animer sur mobile
      if (window.innerWidth <= 768 && el.dataset.aosMobile === 'false') {
        el.classList.add('aos-animate');
        return;
      }
      
      this.observer.observe(el);
    });
  }

  initParallax() {
    if (!this.parallaxElements.length) return;
    
    let ticking = false;
    
    const updateParallax = () => {
      this.parallaxElements.forEach(el => {
        const rect = el.getBoundingClientRect();
        const elementTop = rect.top;
        const elementHeight = rect.height;
        const windowHeight = window.innerHeight;
        
        // Calculer le pourcentage de visibilité
        const scrollProgress = (windowHeight - elementTop) / (windowHeight + elementHeight);
        
        // Appliquer le parallax (déplacement)
        const speed = parseFloat(el.dataset.aosSpeed) || 0.5;
        const translateY = (scrollProgress - 0.5) * 100 * speed;
        
        el.style.transform = `translateY(${translateY}px)`;
      });
      
      ticking = false;
    };
    
    const requestParallaxUpdate = () => {
      if (!ticking) {
        requestAnimationFrame(updateParallax);
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', requestParallaxUpdate, { passive: true });
    updateParallax(); // Initial call
  }

  // Méthode pour rafraîchir (utile pour contenu dynamique)
  refresh() {
    if (this.observer) {
      this.observer.disconnect();
    }
    
    this.elements = document.querySelectorAll('[data-aos]');
    this.parallaxElements = document.querySelectorAll('[data-aos="parallax"]');
    
    this.init();
    this.initParallax();
  }

  // Méthode pour désactiver toutes les animations
  disable() {
    if (this.observer) {
      this.observer.disconnect();
    }
    
    this.elements.forEach(el => {
      el.classList.add('aos-animate');
    });
  }

  // Méthode pour réactiver les animations
  enable() {
    this.elements.forEach(el => {
      el.classList.remove('aos-animate');
    });
    
    this.init();
  }
}

// Initialiser au chargement du DOM
const initScrollAnimations = () => {
  // Vérifier si l'utilisateur préfère réduire les animations
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  
  if (prefersReducedMotion) {
    // Désactiver les animations
    document.querySelectorAll('[data-aos]').forEach(el => {
      el.classList.add('aos-animate');
    });
    return;
  }
  
  // Initialiser les animations
  window.scrollAnimations = new ScrollAnimations({
    threshold: 0.15,
    rootMargin: '0px 0px -50px 0px',
    once: false // Mettre true si tu veux que les animations ne se jouent qu'une fois
  });
};

// Lancer au chargement
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initScrollAnimations);
} else {
  initScrollAnimations();
}

// Rafraîchir lors d'un changement de taille d'écran
let resizeTimeout;
window.addEventListener('resize', () => {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(() => {
    if (window.scrollAnimations) {
      window.scrollAnimations.refresh();
    }
  }, 250);
});

// Exposer pour usage dans Shopify theme editor
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    if (window.scrollAnimations) {
      window.scrollAnimations.refresh();
    }
  });
}